import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Box, Grid, MenuItem, TextField, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import LoaderButton from '@talentinc/gatsby-theme-ecom/components/Buttons/LoaderButton'
import CaptchaProvider from '@talentinc/gatsby-theme-ecom/components/Providers/Captcha'
import useSiteMetadata from '@talentinc/gatsby-theme-ecom/hooks/useSiteMetadata'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import useAxios from '../../hooks/useAxios'
import type {
  PartnerFormFieldKeys,
  PartnerFormFields,
} from '../../types/partnerform'
import {
  businessTypeOptions,
  isValidBusinessType,
  validateSchema,
} from '../../types/partnerform'

const PartnershipsForm = ({ inSection }: { inSection?: boolean }) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const { brandName } = useSiteMetadata()
  const axios = useAxios()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const refData = useRef<PartnerFormFields>({
    partnerships_first_name: '',
    partnerships_last_name: '',
    partnerships_email: '',
    partnerships_job_board: '',
    partnerships_message: '',
    partnerships_business_type: '',
  })
  const [errorState, setErrorState] = useState<
    Record<`${PartnerFormFieldKeys}_error_message`, string | null>
  >({
    partnerships_first_name_error_message: null,
    partnerships_last_name_error_message: null,
    partnerships_email_error_message: null,
    partnerships_job_board_error_message: null,
    partnerships_message_error_message: null,
    partnerships_business_type_error_message: null,
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [submittedTime, setSubmittedTime] = useState<number>(() => Date.now())
  const [submitState, setSubmitState] = useState<'success' | 'fail' | null>(
    null
  )

  const handleChange = useCallback(
    (
        fieldName: keyof PartnerFormFields
      ): React.ChangeEventHandler<HTMLInputElement> =>
      (event) => {
        const { value } = event.target
        if (fieldName === 'partnerships_business_type') {
          if (isValidBusinessType(value)) {
            refData.current[fieldName] = value
          }
        } else {
          refData.current[fieldName] = value
        }
        const field = `${fieldName}_error_message` as const
        setErrorState((prev) => ({ ...prev, [field]: null }))
      },
    []
  )

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()

      if (submitState) {
        setSubmitState(null)
      }

      const errors = validateSchema(refData.current)

      if (Object.values(errors).some((v) => v != null && !!v.length)) {
        return setErrorState(
          // NOTE: apply "t" into every "message error" value and keep the strucutre of the obj
          Object.entries(errors).reduce(
            (acc, [k, v]) => ({
              ...acc,
              [k]: v ? t(v) : null,
            }),
            {} as typeof errors
          )
        )
      }

      setIsLoading(true)

      try {
        const recaptchaToken = await executeRecaptcha?.('partnerships')
        const {
          partnerships_first_name: firstName,
          partnerships_last_name: lastName,
          ...partnershipData
        } = refData.current

        await axios.post('api/v2/msg/transactional/internal', {
          grecaptcha3_tok: recaptchaToken,
          contact_email: partnershipData.partnerships_email,
          contact_comments: partnershipData.partnerships_message,
          contact_name: `${firstName} ${lastName}`,
          site_domain: brandName,
          partner_website: partnershipData.partnerships_job_board,
          business_type: partnershipData.partnerships_business_type,
        })

        refData.current = {
          partnerships_first_name: '',
          partnerships_last_name: '',
          partnerships_email: '',
          partnerships_job_board: '',
          partnerships_message: '',
          partnerships_business_type: '',
        }

        setIsLoading(false)
        setSubmittedTime(Date.now())
        setSubmitState('success')
      } catch (e) {
        setSubmitState('fail')
        setIsLoading(false)
      }
    },
    [axios, brandName, executeRecaptcha, submitState, t]
  )

  const containsInvalidFields = useMemo(
    () => Object.values(errorState).some(Boolean),
    [errorState]
  )

  const businessTypeSelectItems = useMemo(
    () =>
      businessTypeOptions.map(({ labelKey, value }) => (
        <MenuItem key={value} value={value}>
          {t(labelKey)}
        </MenuItem>
      )),
    [t]
  )

  return (
    <form
      key={submittedTime}
      className={classes.form}
      onSubmit={handleSubmit}
      noValidate
    >
      <Typography variant="h3" color="textPrimary">
        {t('partnershipsForm.formHeading')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            className={classes.textField}
            label={t('partnershipsForm.firstName')}
            variant="outlined"
            onChange={handleChange('partnerships_first_name')}
            required
            error={!!errorState.partnerships_first_name_error_message}
            helperText={errorState.partnerships_first_name_error_message}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            className={classes.textField}
            label={t('partnershipsForm.lastName')}
            variant="outlined"
            onChange={handleChange('partnerships_last_name')}
            required
            error={!!errorState.partnerships_last_name_error_message}
            helperText={errorState.partnerships_last_name_error_message}
          />
        </Grid>
      </Grid>
      <TextField
        className={classes.textField}
        label={t('partnershipsForm.email')}
        variant="outlined"
        onChange={handleChange('partnerships_email')}
        type="email"
        required
        error={!!errorState.partnerships_email_error_message}
        helperText={errorState.partnerships_email_error_message}
      />
      <TextField
        className={classes.textField}
        label={t('partnershipsForm.website')}
        variant="outlined"
        onChange={handleChange('partnerships_job_board')}
        type="text"
        required
        error={!!errorState.partnerships_job_board_error_message}
        helperText={errorState.partnerships_job_board_error_message}
      />
      <TextField
        className={classes.textField}
        select
        fullWidth
        variant="outlined"
        label={t('partnershipsForm.businessType')}
        onChange={handleChange('partnerships_business_type')}
        error={!!errorState.partnerships_business_type_error_message}
        helperText={errorState.partnerships_business_type_error_message}
        defaultValue=""
        required
      >
        {businessTypeSelectItems}
      </TextField>
      <TextField
        className={classes.textField}
        InputProps={{
          classes: {
            input: classes.inputLarge,
            root: classes.textFieldLarge,
          },
        }}
        label={t('partnershipsForm.helpYou')}
        multiline
        variant="outlined"
        onChange={handleChange('partnerships_message')}
        required
        error={!!errorState.partnerships_message_error_message}
        helperText={errorState.partnerships_message_error_message}
      />
      <Box className={classes.submitBox}>
        <Box className={classes.submitInfoBox}>
          <Typography className={classes.requiredTR}>
            {t('partnershipsForm.required')}
          </Typography>
        </Box>
        <LoaderButton
          className={classes.button}
          variant="contained"
          type="submit"
          loading={isLoading}
          disabled={containsInvalidFields}
        >
          {t(
            inSection
              ? 'partnershipsForm.submitInSection'
              : 'partnershipsForm.submit'
          )}
        </LoaderButton>
      </Box>
      <Box mt={2}>
        {submitState === 'success' && (
          <Typography variant="body2" className={classes.successText}>
            {t('commonFormMessages.submitSuccess')}
          </Typography>
        )}
        {submitState === 'fail' && (
          <Typography
            variant="body2"
            component="p"
            align="center"
            color="error"
          >
            {t('commonFormMessages.submitFail')}
          </Typography>
        )}
      </Box>
    </form>
  )
}

const PartnershipsFormWithCaptcha: React.FC = () => {
  return (
    <CaptchaProvider>
      <PartnershipsForm />
    </CaptchaProvider>
  )
}

export default PartnershipsFormWithCaptcha

const useStyles = makeStyles()((theme) => ({
  form: {
    background: theme.colors.white['100'],
    border: `1px solid ${theme.colors.gray['a200']}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'left',
  },
  inputLarge: {
    maxHeight: '9rem',
    minHeight: '9rem',
    textAlign: 'justify',
    overflow: 'auto !important',
    paddingRight: theme.spacing(1),
  },
  textFieldLarge: {
    alignItems: 'flex-start',
  },
  dropdown: {
    paddingInline: theme.spacing(2),
  },
  successText: {
    color: theme.palette.success.main,
  },
  textField: {
    marginTop: '1.25em',
    width: '100%',
    '& .MuiFormLabel-root': {
      [theme.breakpoints.down('sm')]: {
        width: '90%!important',
      },
    },
  },
  submitBox: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1.25em',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  submitInfoBox: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '68%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  requiredTR: {
    color: theme.palette.secondary.dark,
  },
  button: {
    minWidth: 'fit-content',
    [theme.breakpoints.down('md')]: {
      marginTop: '1.25em',
      width: '100%',
    },
  },
}))
